import { LayoutDashboard, Inbox, Settings, FileText, Blocks, LogOut, PanelLeftClose, PanelLeftOpen, Globe, Layers, ScrollText, ShieldCheck, Volume2, VolumeX, ClipboardList } from "lucide-react";
import { useSettings } from "@/context/AppSettings";

const ITEMS = [
  { id: "dashboard", label: "Дашборд", icon: LayoutDashboard },
  { id: "leads", label: "Заявки", icon: Inbox },
  { id: "registry", label: "Реестр", icon: ClipboardList },
  { id: "landings", label: "Лендинги", icon: Layers },
  { id: "settings", label: "Настройки", icon: Settings },
  { id: "documents", label: "Документы", icon: FileText },
  { id: "chancellery", label: "Канцелярия", icon: ScrollText },
  { id: "aml", label: "AML / KYC", icon: ShieldCheck },
  { id: "integrations", label: "Интеграции", icon: Blocks },
];

const itemClass = (active) =>
  `flex items-center gap-3 rounded-2xl transition-all duration-300 ${
    active
      ? "bg-[#D4AF37]/12 font-semibold text-[#F3E5AB]"
      : "text-slate-400 hover:bg-white/[0.04] hover:text-slate-100"
  }`;

const LeadBadges = ({ summary, compact = false }) => {
  const newCount = summary?.counts?.new || 0;
  if (!newCount) return null;
  return (
    <span className={`ml-auto flex items-center ${compact ? "text-[10px]" : "text-[11px]"}`} aria-label={`Новые заявки: ${newCount}.`}>
      <span data-testid="sidebar-new-leads-count" title="Новые заявки" className="inline-flex min-w-5 items-center justify-center rounded-full border border-red-300/50 bg-red-500/20 px-1.5 py-0.5 font-semibold text-red-200 shadow-[0_0_18px_rgba(248,113,113,0.22)]">{newCount}</span>
    </span>
  );
};

export const Sidebar = ({ tab, setTab, collapsed, setCollapsed, onLogout, leadSummary, leadSoundEnabled, setLeadSoundEnabled, onLeadAlertsTest, notificationPermission }) => {
  const settings = useSettings();
  const brand = settings.company_name || "MELNIK Commercial Brokers";
  const SoundIcon = leadSoundEnabled ? Volume2 : VolumeX;

  return (
    <>
      <aside
        data-testid="admin-sidebar"
        className={`sticky top-0 hidden h-screen shrink-0 flex-col border-r border-white/8 bg-[#0D0F14] transition-all duration-500 md:flex ${
          collapsed ? "w-[76px]" : "w-64"
        }`}
      >
        <div className="flex items-center gap-3 px-5 pb-6 pt-6">
          {!collapsed && (
            <span className="flex items-center gap-2" aria-label={brand}><img src="/melnik-monogram.png" alt="" className="h-10 w-auto object-contain" /><span className="flex flex-col leading-none"><span className="font-serif text-sm tracking-[0.11em] text-[#F5F0E4]">MELNIK</span><span className="mt-1 text-[0.38rem] font-semibold tracking-[0.2em] text-[#D4AF37]">COMMERCIAL BROKERS</span></span></span>
          )}
        </div>

        <nav className="flex-1 space-y-1.5 px-3">
          {ITEMS.map((item) => (
            <button
              key={item.id}
              data-testid={`admin-nav-${item.id}`}
              onClick={() => setTab(item.id)}
              className={`${itemClass(tab === item.id)} w-full px-3.5 py-3 text-left text-sm`}
              title={collapsed && item.id === "leads" ? `Новых заявок: ${leadSummary?.counts?.new || 0}` : undefined}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {!collapsed && item.label}
              {item.id === "leads" && <LeadBadges summary={leadSummary} />}
            </button>
          ))}
        </nav>

        <div className="space-y-1.5 border-t border-white/8 p-3">
          <button
            type="button"
            data-testid="lead-notification-sound-toggle"
            onClick={setLeadSoundEnabled}
            title={leadSoundEnabled ? "Звук новых заявок включён" : "Звук новых заявок выключен"}
            className="flex w-full items-center gap-3 rounded-2xl px-3.5 py-3 text-sm text-slate-400 transition-colors hover:bg-white/[0.04] hover:text-slate-100"
          >
            <SoundIcon className={`h-5 w-5 shrink-0 ${leadSoundEnabled ? "text-[#D4AF37]" : ""}`} />
            {!collapsed && (leadSoundEnabled ? "Звук заявок: вкл." : "Звук заявок: вык.")}
          </button>
          {!collapsed && <button type="button" data-testid="lead-notification-test" onClick={onLeadAlertsTest} title={notificationPermission === "denied" ? "Browser-уведомления запрещены в настройках браузера; звук всё равно будет проверен." : "Проверить звук и browser-уведомление"} className="flex w-full items-center gap-3 rounded-2xl px-3.5 py-2.5 text-left text-xs text-slate-500 transition-colors hover:bg-white/[0.04] hover:text-slate-200"><Volume2 className="h-4 w-4 shrink-0 text-[#D4AF37]" />Проверить уведомление</button>}
          <a
            href="/"
            data-testid="admin-nav-site"
            className="flex w-full items-center gap-3 rounded-2xl px-3.5 py-3 text-sm text-slate-400 transition-colors hover:bg-white/[0.04] hover:text-slate-100"
          >
            <Globe className="h-5 w-5 shrink-0" />
            {!collapsed && "На сайт"}
          </a>
          <button
            data-testid="admin-nav-collapse"
            onClick={() => setCollapsed(!collapsed)}
            className="flex w-full items-center gap-3 rounded-2xl px-3.5 py-3 text-sm text-slate-400 transition-colors hover:bg-white/[0.04] hover:text-slate-100"
          >
            {collapsed ? <PanelLeftOpen className="h-5 w-5 shrink-0" /> : <PanelLeftClose className="h-5 w-5 shrink-0" />}
            {!collapsed && "Свернуть меню"}
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={onLogout}
            className="flex w-full items-center gap-3 rounded-2xl px-3.5 py-3 text-sm text-slate-400 transition-colors hover:bg-red-500/10 hover:text-red-300"
          >
            <LogOut className="h-5 w-5 shrink-0" />
            {!collapsed && "Выйти"}
          </button>
        </div>
      </aside>

      <header className="sticky top-0 z-30 border-b border-white/8 bg-[#0D0F14]/95 backdrop-blur md:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="flex items-center gap-1.5" aria-label={brand}><img src="/melnik-monogram.png" alt="" className="h-8 w-auto object-contain" /><span className="font-serif text-xs tracking-[0.1em] text-[#F5F0E4]">MELNIK</span></span>
          </div>
          <div className="flex items-center gap-1.5">
            <button type="button" data-testid="mobile-lead-notification-sound-toggle" onClick={setLeadSoundEnabled} aria-label={leadSoundEnabled ? "Выключить звук новых заявок" : "Включить звук новых заявок"} className="rounded-xl p-2.5 text-slate-400 transition-colors hover:bg-white/[0.05] hover:text-slate-100"><SoundIcon className={`h-5 w-5 ${leadSoundEnabled ? "text-[#D4AF37]" : ""}`} /></button>
            <a
              href="/"
              aria-label="Открыть сайт"
              className="rounded-xl p-2.5 text-slate-400 transition-colors hover:bg-white/[0.05] hover:text-slate-100"
            >
              <Globe className="h-5 w-5" />
            </a>
            <button
              onClick={onLogout}
              aria-label="Выйти из панели"
              className="rounded-xl p-2.5 text-slate-400 transition-colors hover:bg-red-500/10 hover:text-red-300"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
        <nav className="flex gap-2 overflow-x-auto px-4 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              className={`${itemClass(tab === item.id)} shrink-0 px-3 py-2 text-xs`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              {item.id === "leads" && <LeadBadges summary={leadSummary} compact />}
            </button>
          ))}
        </nav>
      </header>
    </>
  );
};
